import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, 
  LogOut, 
  Plus,
  Pencil,
  Trash2,
  X,
  Shield,
  UserCog,
  Mail,
  Lock,
  User,
  Check,
  Ban,
  CheckCircle,
  Star,
  Globe,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
  Send,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileStack,
  Eye,
  UserCheck,
  Settings,
  Coins,
  Search,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

const roleConfig = {
  admin: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30", icon: Shield, label: "Admin" },
  manager: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30", icon: UserCog, label: "Manager" },
  creator: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/30", icon: Star, label: "Creator" },
  fan: { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30", icon: User, label: "Fan" }
};

const UserManagementPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("staff"); // "staff", "creators", or "fans"
  const [users, setUsers] = useState([]);
  const [creators, setCreators] = useState([]);
  const [fans, setFans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "manager" });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [togglingLandingId, setTogglingLandingId] = useState(null);
  const [sendingCredentialsId, setSendingCredentialsId] = useState(null);
  const [resettingCredentialsId, setResettingCredentialsId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [managerStats, setManagerStats] = useState({}); // Map of manager_id -> assigned_applications count
  
  // Pagination state for creators and fans
  const [creatorsPage, setCreatorsPage] = useState(1);
  const [creatorsTotalPages, setCreatorsTotalPages] = useState(1);
  const [creatorsTotal, setCreatorsTotal] = useState(0);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [creatorsSearch, setCreatorsSearch] = useState("");
  
  const [fansPage, setFansPage] = useState(1);
  const [fansTotalPages, setFansTotalPages] = useState(1);
  const [fansTotal, setFansTotal] = useState(0);
  const [fansLoading, setFansLoading] = useState(false);
  const [fansSearch, setFansSearch] = useState("");
  
  // Credits editing state (for fans)
  const [editingCreditsId, setEditingCreditsId] = useState(null);
  const [editCreditsValue, setEditCreditsValue] = useState("");
  const [savingCredits, setSavingCredits] = useState(false);

  // Credits editing state (for creators)
  const [editingCreatorCreditsId, setEditingCreatorCreditsId] = useState(null);
  const [editCreatorCreditsValue, setEditCreatorCreditsValue] = useState("");
  const [savingCreatorCredits, setSavingCreatorCredits] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role !== "admin") {
      navigate("/admin");
      return;
    }
    
    setCurrentUser(userData);
    fetchUsers();
    fetchCreators(1, "");
    fetchFans(1, "");
    fetchManagerStats();
  }, [navigate]);

  // Refetch creators when page or search changes
  useEffect(() => {
    if (activeTab === "creators") {
      fetchCreators(creatorsPage, creatorsSearch);
    }
  }, [creatorsPage, creatorsSearch, activeTab]);

  // Refetch fans when page or search changes
  useEffect(() => {
    if (activeTab === "fans") {
      fetchFans(fansPage, fansSearch);
    }
  }, [fansPage, fansSearch, activeTab]);

  // Debounced search for creators
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "creators") {
        setCreatorsPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [creatorsSearch]);

  // Debounced search for fans
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "fans") {
        setFansPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [fansSearch]);

  const fetchUsers = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/users`, {
        headers: {}
      });
      setUsers(response.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchManagerStats = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/managers/stats`, {
        headers: {}
      });
      // Convert array to map for easy lookup
      const statsMap = {};
      response.data.forEach(m => {
        statsMap[m.id] = m.assigned_applications;
      });
      setManagerStats(statsMap);
    } catch (err) {
      // Failed to fetch manager stats - continue with empty stats
      setManagerStats({});
    }
  };

  const fetchCreators = async (page = 1, search = "") => {
    setCreatorsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: itemsPerPage });
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/creators?${params}`, {
        headers: {}
      });
      setCreators(response.data.creators || []);
      setCreatorsTotalPages(response.data.total_pages || 1);
      setCreatorsTotal(response.data.total || 0);
    } catch (err) {
      // Failed to fetch creators
      setCreators([]);
    } finally {
      setCreatorsLoading(false);
    }
  };

  const fetchFans = async (page = 1, search = "") => {
    setFansLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: itemsPerPage });
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/fans?${params}`, {
        headers: {}
      });
      setFans(response.data.fans || []);
      setFansTotalPages(response.data.total_pages || 1);
      setFansTotal(response.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch fans:", err);
      setFans([]);
    } finally {
      setFansLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout failed:", err.message);
    }
    localStorage.removeItem("user");
    navigate("/login");
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "", role: "manager" });
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: "", role: user.role });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "", role: "manager" });
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);

    try {
      
      
      if (editingUser) {
        // Update user
        const updateData = {};
        if (formData.name !== editingUser.name) updateData.name = formData.name;
        if (formData.email !== editingUser.email) updateData.email = formData.email;
        if (formData.role !== editingUser.role) updateData.role = formData.role;
        if (formData.password) updateData.password = formData.password;
        
        await axios.patch(`${API}/admin/users/${editingUser.id}`, updateData, {
          headers: {}
        });
      } else {
        // Create user
        if (!formData.password) {
          setFormError("Password is required for new users");
          setSubmitting(false);
          return;
        }
        await axios.post(`${API}/admin/users`, formData, {
          headers: {}
        });
      }
      
      closeModal();
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }
    
    setDeletingId(userId);
    try {
      
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: {}
      });
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleUserAccess = async (userId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }
    
    setTogglingId(userId);
    try {
      
      const response = await axios.patch(`${API}/admin/users/${userId}/toggle-access`, {}, {
        headers: {}
      });
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_active: response.data.is_active } : u
      ));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to toggle user access");
    } finally {
      setTogglingId(null);
    }
  };

  const deleteCreator = async (creatorId) => {
    if (!window.confirm("Are you sure you want to delete this creator? This action cannot be undone.")) {
      return;
    }
    
    setDeletingId(creatorId);
    try {
      
      await axios.delete(`${API}/admin/creators/${creatorId}`, {
        headers: {}
      });
      setCreators(prev => prev.filter(c => c.id !== creatorId));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete creator");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleCreatorLandingPage = async (creatorId, currentActive) => {
    setTogglingLandingId(creatorId);
    try {
      
      await axios.patch(`${API}/admin/creators/${creatorId}/landing-page`, 
        { landing_page_active: !currentActive },
        { headers: {} }
      );
      
      setCreators(prev => prev.map(c => 
        c.id === creatorId ? { ...c, landing_page_active: !currentActive } : c
      ));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to toggle landing page");
    } finally {
      setTogglingLandingId(null);
    }
  };

  const sendCredentials = async (creatorId, creatorEmail) => {
    if (!window.confirm(`Send login credentials to ${creatorEmail}?`)) {
      return;
    }
    
    setSendingCredentialsId(creatorId);
    try {
      
      const response = await axios.post(
        `${API}/admin/creators/${creatorId}/send-credentials`,
        {},
        { headers: {} }
      );
      
      alert(response.data.message);
      
      // Update local state
      setCreators(prev => prev.map(c => 
        c.id === creatorId ? { ...c, credentials_sent: true } : c
      ));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send credentials");
    } finally {
      setSendingCredentialsId(null);
    }
  };

  const resetCredentials = async (creatorId) => {
    if (!window.confirm("Reset this creator's password? You'll need to send new credentials afterwards.")) {
      return;
    }
    
    setResettingCredentialsId(creatorId);
    try {
      
      const response = await axios.post(
        `${API}/admin/creators/${creatorId}/reset-credentials`,
        {},
        { headers: {} }
      );
      
      alert(response.data.message);
      
      // Update local state
      setCreators(prev => prev.map(c => 
        c.id === creatorId ? { ...c, credentials_sent: false } : c
      ));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reset credentials");
    } finally {
      setResettingCredentialsId(null);
    }
  };

  const impersonateUser = async (userId, userName) => {
    if (!window.confirm(`Impersonate ${userName}? You will be logged in as this user.`)) {
      return;
    }
    
    setImpersonatingId(userId);
    try {
      
      const response = await axios.post(
        `${API}/admin/impersonate/${userId}`,
        {},
        { headers: {} }
      );
      
      // Store original admin credentials in sessionStorage
      // Store impersonation state in sessionStorage (for UI purposes only)
      sessionStorage.setItem("impersonation", JSON.stringify({
        originalUser: currentUser,
        impersonatedUser: response.data.user,
        impersonatedBy: response.data.impersonated_by
      }));
      
      // Store user info (token is in httpOnly cookie set by server)
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Redirect based on user role
      if (response.data.user.role === "creator" || response.data.user.role === "fan") {
        navigate("/app/profile");
      } else {
        navigate("/admin");
      }
      
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to impersonate user");
    } finally {
      setImpersonatingId(null);
    }
  };

  // Update fan credits
  const updateCredits = async (fanId) => {
    const credits = parseInt(editCreditsValue);
    if (isNaN(credits) || credits < 0) {
      alert("Please enter a valid non-negative number");
      return;
    }
    
    setSavingCredits(true);
    try {
      
      await axios.patch(
        `${API}/admin/users/${fanId}/credits`,
        { credits },
        { headers: {} }
      );
      
      // Update local state
      setFans(prev => prev.map(f => 
        f.id === fanId ? { ...f, credits } : f
      ));
      setEditingCreditsId(null);
      setEditCreditsValue("");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update credits");
    } finally {
      setSavingCredits(false);
    }
  };

  // Update creator credits
  const updateCreatorCredits = async (creatorId) => {
    const credits = parseInt(editCreatorCreditsValue);
    if (isNaN(credits) || credits < 0) {
      alert("Please enter a valid non-negative number");
      return;
    }
    
    setSavingCreatorCredits(true);
    try {
      
      await axios.patch(
        `${API}/admin/users/${creatorId}/credits`,
        { credits },
        { headers: {} }
      );
      
      // Update local state
      setCreators(prev => prev.map(c => 
        c.id === creatorId ? { ...c, credits } : c
      ));
      setEditingCreatorCreditsId(null);
      setEditCreatorCreditsValue("");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update credits");
    } finally {
      setSavingCreatorCredits(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Pagination logic (only for staff, creators/fans are paginated from backend)
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = users.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-900" data-testid="user-management-page">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                <span className="text-white">HotPeek</span>
              </h1>
              <nav className="flex items-center gap-1">
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-applications"
                >
                  <FileStack size={18} />
                  <span className="hidden sm:inline">Applications</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center gap-2 px-3 py-2 text-white bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-users"
                >
                  <Users size={18} />
                  <span className="hidden sm:inline">Users</span>
                </Link>
                <Link
                  to="/admin/settings"
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-settings"
                >
                  <Settings size={18} />
                  <span className="hidden sm:inline">Settings</span>
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{currentUser?.name}</p>
                <p className="text-gray-500 text-sm">{currentUser?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title and Add Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Users</h2>
            <p className="text-gray-500 text-sm">
              {activeTab === "staff" ? `${users.length} staff members` : activeTab === "creators" ? `${creatorsTotal} creators` : `${fansTotal} fans`}
            </p>
          </div>
          {activeTab === "staff" && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
              data-testid="add-user-btn"
            >
              <Plus size={18} />
              Add User
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setActiveTab("staff"); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "staff"
                ? "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                : "bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:bg-gray-700/50"
            }`}
            data-testid="tab-staff"
          >
            <div className="flex items-center gap-2">
              <Shield size={16} />
              Staff ({users.length})
            </div>
          </button>
          <button
            onClick={() => { setActiveTab("creators"); setCurrentPage(1); setCreatorsPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "creators"
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                : "bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:bg-gray-700/50"
            }`}
            data-testid="tab-creators"
          >
            <div className="flex items-center gap-2">
              <Star size={16} />
              Creators ({creatorsTotal})
            </div>
          </button>
          <button
            onClick={() => { setActiveTab("fans"); setCurrentPage(1); setFansPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "fans"
                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                : "bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:bg-gray-700/50"
            }`}
            data-testid="tab-fans"
          >
            <div className="flex items-center gap-2">
              <User size={16} />
              Fans ({fansTotal})
            </div>
          </button>
        </div>

        {/* Search box for Creators and Fans */}
        {(activeTab === "creators" || activeTab === "fans") && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={activeTab === "creators" ? creatorsSearch : fansSearch}
                onChange={(e) => {
                  if (activeTab === "creators") {
                    setCreatorsSearch(e.target.value);
                  } else {
                    setFansSearch(e.target.value);
                  }
                }}
                className="w-full max-w-md pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                data-testid={`search-${activeTab}`}
              />
            </div>
          </div>
        )}

        {/* Staff Users List */}
        {activeTab === "staff" && (
        <div className="space-y-4">
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500">No staff users found</p>
            </div>
          ) : (
            paginatedUsers.map((user, index) => {
              const role = roleConfig[user.role] || roleConfig.manager;
              const RoleIcon = role.icon;
              const isCurrentUser = user.id === currentUser?.id;
              const isActive = user.is_active !== false;
              
              return (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className={`bg-gray-800/50 rounded-xl border ${isCurrentUser ? 'border-[#00A8E8]/50' : !isActive ? 'border-red-500/30 opacity-60' : 'border-gray-700/50'} overflow-hidden hover:border-gray-600/50 transition-colors`}
                  data-testid={`user-card-${user.id}`}
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${!isActive ? 'bg-red-500/10' : role.bg}`}>
                          {!isActive ? (
                            <Ban size={24} className="text-red-400" />
                          ) : (
                            <RoleIcon size={24} className={role.text} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className={`text-lg font-semibold ${!isActive ? 'text-gray-500 line-through' : 'text-white'}`}>{user.name}</h3>
                            {isCurrentUser && (
                              <span className="text-xs text-[#00A8E8] bg-[#00A8E8]/10 px-2 py-0.5 rounded-full">You</span>
                            )}
                            {!isActive && (
                              <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full border border-red-500/30">Deactivated</span>
                            )}
                            {/* Assigned Applications Count */}
                            {managerStats[user.id] !== undefined && managerStats[user.id] > 0 && (
                              <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30" title="Assigned applications">
                                {managerStats[user.id]} assigned
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Mail size={14} />
                              {user.email}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${role.bg} ${role.text} ${role.border}`}>
                              {role.label}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Created: {formatDate(user.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                          title="Edit user"
                          data-testid={`edit-btn-${user.id}`}
                        >
                          <Pencil size={18} />
                        </button>
                        {!isCurrentUser && (
                          <>
                            <button
                              onClick={() => deleteUser(user.id)}
                              disabled={deletingId === user.id}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete user"
                              data-testid={`delete-btn-${user.id}`}
                            >
                              {deletingId === user.id ? (
                                <div className="w-[18px] h-[18px] border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                              ) : (
                                <Trash2 size={18} />
                              )}
                            </button>
                            <button
                              onClick={() => toggleUserAccess(user.id, isActive)}
                              disabled={togglingId === user.id}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                isActive 
                                  ? 'text-gray-400 hover:text-orange-500 hover:bg-orange-500/10' 
                                  : 'text-green-400 hover:text-green-500 hover:bg-green-500/10'
                              }`}
                              title={isActive ? "Deactivate user" : "Activate user"}
                              data-testid={`toggle-btn-${user.id}`}
                            >
                              {togglingId === user.id ? (
                                <div className="w-[18px] h-[18px] border-2 border-gray-500/30 border-t-gray-500 rounded-full animate-spin" />
                              ) : isActive ? (
                                <Ban size={18} />
                              ) : (
                                <CheckCircle size={18} />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
        )}

        {/* Creators List */}
        {activeTab === "creators" && (
          <div className="space-y-4">
            {creatorsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : creators.length === 0 ? (
              <div className="text-center py-12">
                <Star size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-500">No creators found</p>
                <p className="text-gray-600 text-sm mt-2">
                  {creatorsSearch ? "Try a different search term" : "Create creators from applications in the Dashboard"}
                </p>
              </div>
            ) : (
              creators.map((creator, index) => {
                const role = roleConfig.creator;
                const RoleIcon = role.icon;
                
                return (
                  <motion.div
                    key={creator.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden hover:border-gray-600/50 transition-colors"
                    data-testid={`creator-card-${creator.id}`}
                  >
                    <div className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${role.bg}`}>
                            <RoleIcon size={24} className={role.text} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-white">{creator.name}</h3>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${role.bg} ${role.text} ${role.border}`}>
                                {role.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Mail size={14} />
                                {creator.email}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-gray-500">
                                Created: {formatDate(creator.created_at)}
                              </span>
                              <span className="text-xs text-gray-500">
                                • {creator.content_count || 0} content items
                              </span>
                              {/* Editable Credits */}
                              {editingCreatorCreditsId === creator.id ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    value={editCreatorCreditsValue}
                                    onChange={(e) => setEditCreatorCreditsValue(e.target.value)}
                                    className="w-20 px-2 py-0.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-amber-500"
                                    placeholder="Credits"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => updateCreatorCredits(creator.id)}
                                    disabled={savingCreatorCredits}
                                    className="p-0.5 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                                    title="Save"
                                  >
                                    {savingCreatorCredits ? (
                                      <div className="w-3 h-3 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                                    ) : (
                                      <Check size={14} />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingCreatorCreditsId(null);
                                      setEditCreatorCreditsValue("");
                                    }}
                                    className="p-0.5 text-gray-400 hover:bg-gray-600 rounded transition-colors"
                                    title="Cancel"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : (
                                <span 
                                  className="flex items-center gap-1 text-xs text-amber-400 font-medium cursor-pointer hover:text-amber-300 transition-colors"
                                  onClick={() => {
                                    setEditingCreatorCreditsId(creator.id);
                                    setEditCreatorCreditsValue(creator.credits?.toString() || "0");
                                  }}
                                  title="Click to edit credits"
                                >
                                  <Coins size={12} />
                                  {creator.credits || 0} credits
                                  <Pencil size={10} className="opacity-50" />
                                </span>
                              )}
                              {creator.landing_page_slug && (
                                <a 
                                  href={`/c/${creator.landing_page_slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                                >
                                  <Globe size={12} />
                                  /c/{creator.landing_page_slug}
                                  <ExternalLink size={10} />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* View Profile Button */}
                          <Link
                            to={`/admin/creators/${creator.id}`}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/30 hover:bg-gray-700 hover:text-white transition-colors"
                            data-testid={`view-profile-${creator.id}`}
                          >
                            <Eye size={14} />
                            View Profile
                          </Link>
                          
                          {/* Send Credentials Button */}
                          {!creator.credentials_sent ? (
                            <button
                              onClick={() => sendCredentials(creator.id, creator.email)}
                              disabled={sendingCredentialsId === creator.id}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                              title="Send login credentials via email"
                              data-testid={`send-credentials-${creator.id}`}
                            >
                              {sendingCredentialsId === creator.id ? (
                                <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                              ) : (
                                <Send size={14} />
                              )}
                              Send Credentials
                            </button>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/30">
                                <Check size={12} />
                                Credentials Sent
                              </span>
                              <button
                                onClick={() => resetCredentials(creator.id)}
                                disabled={resettingCredentialsId === creator.id}
                                className="p-1.5 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors disabled:opacity-50"
                                title="Reset password and send new credentials"
                                data-testid={`reset-credentials-${creator.id}`}
                              >
                                {resettingCredentialsId === creator.id ? (
                                  <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                                ) : (
                                  <RefreshCw size={14} />
                                )}
                              </button>
                            </div>
                          )}
                          
                          {/* Landing Page Toggle */}
                          <button
                            onClick={() => toggleCreatorLandingPage(creator.id, creator.landing_page_active)}
                            disabled={togglingLandingId === creator.id}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                              creator.landing_page_active
                                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                : "bg-gray-700/50 text-gray-400 border border-gray-600/30"
                            }`}
                            title={creator.landing_page_active ? "Deactivate landing page" : "Activate landing page"}
                            data-testid={`toggle-landing-${creator.id}`}
                          >
                            {togglingLandingId === creator.id ? (
                              <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : creator.landing_page_active ? (
                              <ToggleRight size={14} />
                            ) : (
                              <ToggleLeft size={14} />
                            )}
                            {creator.landing_page_active ? "Active" : "Inactive"}
                          </button>
                          {/* Impersonate Creator */}
                          <button
                            onClick={() => impersonateUser(creator.id, creator.name)}
                            disabled={impersonatingId === creator.id}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
                            title="Impersonate this creator"
                            data-testid={`impersonate-creator-${creator.id}`}
                          >
                            {impersonatingId === creator.id ? (
                              <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : (
                              <UserCheck size={14} />
                            )}
                            Impersonate
                          </button>
                          {/* Delete Creator */}
                          <button
                            onClick={() => deleteCreator(creator.id)}
                            disabled={deletingId === creator.id}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete creator"
                            data-testid={`delete-creator-${creator.id}`}
                          >
                            {deletingId === creator.id ? (
                              <div className="w-[18px] h-[18px] border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                            ) : (
                              <Trash2 size={18} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
            
            {/* Creators Pagination */}
            {creatorsTotalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-700/50">
                <p className="text-sm text-gray-500">
                  Showing {((creatorsPage - 1) * itemsPerPage) + 1} to {Math.min(creatorsPage * itemsPerPage, creatorsTotal)} of {creatorsTotal} creators
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCreatorsPage(Math.max(1, creatorsPage - 1))}
                    disabled={creatorsPage === 1 || creatorsLoading}
                    className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-gray-400 text-sm px-3">
                    Page {creatorsPage} of {creatorsTotalPages}
                  </span>
                  <button
                    onClick={() => setCreatorsPage(Math.min(creatorsTotalPages, creatorsPage + 1))}
                    disabled={creatorsPage === creatorsTotalPages || creatorsLoading}
                    className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fans List */}
        {activeTab === "fans" && (
          <div className="space-y-4">
            {fansLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
              </div>
            ) : fans.length === 0 ? (
              <div className="text-center py-12">
                <User size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-500">No fans found</p>
                <p className="text-gray-600 text-sm mt-2">
                  {fansSearch ? "Try a different search term" : "Fans register through the public registration page"}
                </p>
              </div>
            ) : (
              fans.map((fan, index) => {
                const role = roleConfig.fan;
                const RoleIcon = role.icon;
                const isActive = fan.is_active !== false;
                
                return (
                  <motion.div
                    key={fan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className={`bg-gray-800/50 rounded-xl border ${!isActive ? 'border-red-500/30 opacity-60' : 'border-gray-700/50'} overflow-hidden hover:border-gray-600/50 transition-colors`}
                    data-testid={`fan-card-${fan.id}`}
                  >
                    <div className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          {/* Profile Image */}
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden ${!isActive ? 'bg-red-500/10' : role.bg}`}>
                            {fan.profile_image ? (
                              <img 
                                src={fan.profile_image} 
                                alt={fan.name}
                                className="w-full h-full object-cover"
                              />
                            ) : !isActive ? (
                              <Ban size={24} className="text-red-400" />
                            ) : (
                              <RoleIcon size={24} className={role.text} />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`text-lg font-semibold ${!isActive ? 'text-gray-500 line-through' : 'text-white'}`}>{fan.name}</h3>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${role.bg} ${role.text} ${role.border}`}>
                                {role.label}
                              </span>
                              {fan.is_verified && (
                                <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/30">
                                  <Check size={10} className="inline mr-1" />
                                  Verified
                                </span>
                              )}
                              {!isActive && (
                                <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full border border-red-500/30">Deactivated</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Mail size={14} />
                                {fan.email}
                              </span>
                              {fan.location && (
                                <span className="text-gray-500">• {fan.location}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-gray-500">
                                Created: {formatDate(fan.created_at)}
                              </span>
                              <span className="text-xs text-gray-500">
                                • {fan.content_count} content items
                              </span>
                              {fan.instagram && (
                                <span className="text-xs text-pink-400">@{fan.instagram.replace('@', '')}</span>
                              )}
                            </div>
                            {/* Credits Display */}
                            <div className="flex items-center gap-2 mt-2">
                              <Coins size={14} className="text-amber-400" />
                              {editingCreditsId === fan.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    value={editCreditsValue}
                                    onChange={(e) => setEditCreditsValue(e.target.value)}
                                    className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                                    placeholder="Credits"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => updateCredits(fan.id)}
                                    disabled={savingCredits}
                                    className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                                    title="Save"
                                  >
                                    {savingCredits ? (
                                      <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                                    ) : (
                                      <Check size={16} />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingCreditsId(null);
                                      setEditCreditsValue("");
                                    }}
                                    className="p-1 text-gray-400 hover:bg-gray-600 rounded transition-colors"
                                    title="Cancel"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-amber-400 font-medium">{fan.credits || 0} credits</span>
                                  <button
                                    onClick={() => {
                                      setEditingCreditsId(fan.id);
                                      setEditCreditsValue(fan.credits?.toString() || "0");
                                    }}
                                    className="p-1 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-colors"
                                    title="Edit credits"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* View Profile */}
                          <button
                            onClick={() => navigate(`/admin/fans/${fan.id}`)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 transition-colors"
                            title="View profile & manage content"
                            data-testid={`view-fan-${fan.id}`}
                          >
                            <Eye size={14} />
                            View
                          </button>
                          {/* Impersonate Fan */}
                          <button
                            onClick={() => impersonateUser(fan.id, fan.name)}
                            disabled={impersonatingId === fan.id}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
                            title="Impersonate this fan"
                            data-testid={`impersonate-fan-${fan.id}`}
                          >
                            {impersonatingId === fan.id ? (
                              <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : (
                              <UserCheck size={14} />
                            )}
                            Impersonate
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
            
            {/* Fans Pagination */}
            {fansTotalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-700/50">
                <p className="text-sm text-gray-500">
                  Showing {((fansPage - 1) * itemsPerPage) + 1} to {Math.min(fansPage * itemsPerPage, fansTotal)} of {fansTotal} fans
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFansPage(Math.max(1, fansPage - 1))}
                    disabled={fansPage === 1 || fansLoading}
                    className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="fans-prev-page"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-gray-400 text-sm px-3">
                    Page {fansPage} of {fansTotalPages}
                  </span>
                  <button
                    onClick={() => setFansPage(Math.min(fansTotalPages, fansPage + 1))}
                    disabled={fansPage === fansTotalPages || fansLoading}
                    className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="fans-next-page"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Staff Pagination */}
        {activeTab === "staff" && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-700/50">
            <p className="text-sm text-gray-500">
              Showing {startIndex + 1}-{Math.min(endIndex, users.length)} of {users.length} staff
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="staff-prev-page"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-[#00A8E8] text-white"
                          : "text-gray-400 hover:text-white hover:bg-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="staff-next-page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">
                  {editingUser ? "Edit User" : "Create User"}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    {formError}
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
                      placeholder="John Doe"
                      data-testid="user-name-input"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
                      placeholder="john@example.com"
                      data-testid="user-email-input"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password {editingUser && <span className="text-gray-500">(leave blank to keep current)</span>}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
                      placeholder={editingUser ? "••••••••" : "Enter password"}
                      data-testid="user-password-input"
                    />
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: "admin" })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.role === "admin"
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-gray-600 hover:border-gray-500"
                      }`}
                      data-testid="role-admin-btn"
                    >
                      <Shield size={24} className={formData.role === "admin" ? "text-purple-400 mx-auto" : "text-gray-500 mx-auto"} />
                      <p className={`mt-2 font-medium ${formData.role === "admin" ? "text-purple-400" : "text-gray-400"}`}>Admin</p>
                      <p className="text-xs text-gray-500 mt-1">Full system access</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: "manager" })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.role === "manager"
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-gray-600 hover:border-gray-500"
                      }`}
                      data-testid="role-manager-btn"
                    >
                      <UserCog size={24} className={formData.role === "manager" ? "text-blue-400 mx-auto" : "text-gray-500 mx-auto"} />
                      <p className={`mt-2 font-medium ${formData.role === "manager" ? "text-blue-400" : "text-gray-400"}`}>Manager</p>
                      <p className="text-xs text-gray-500 mt-1">Manage applications</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: "creator" })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.role === "creator"
                          ? "border-cyan-500 bg-cyan-500/10"
                          : "border-gray-600 hover:border-gray-500"
                      }`}
                      data-testid="role-creator-btn"
                    >
                      <Star size={24} className={formData.role === "creator" ? "text-cyan-400 mx-auto" : "text-gray-500 mx-auto"} />
                      <p className={`mt-2 font-medium ${formData.role === "creator" ? "text-cyan-400" : "text-gray-400"}`}>Creator</p>
                      <p className="text-xs text-gray-500 mt-1">Content creator account</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: "fan" })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.role === "fan"
                          ? "border-pink-500 bg-pink-500/10"
                          : "border-gray-600 hover:border-gray-500"
                      }`}
                      data-testid="role-fan-btn"
                    >
                      <User size={24} className={formData.role === "fan" ? "text-pink-400 mx-auto" : "text-gray-500 mx-auto"} />
                      <p className={`mt-2 font-medium ${formData.role === "fan" ? "text-pink-400" : "text-gray-400"}`}>Fan</p>
                      <p className="text-xs text-gray-500 mt-1">Regular user account</p>
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="save-user-btn"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check size={18} />
                        {editingUser ? "Save Changes" : "Create User"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagementPage;
